/**
 * TablesPanel.tsx — the editable demo tables grid.
 *
 * A horizontal panel docked under the canvas that shows the project's editable
 * domain tables (orders, staff, inventory, params). Each table renders as a
 * hand-rolled grid (no <table> — matching the codebase's div-row convention);
 * cells are editable inline; writes PATCH the backend, and the model picks up
 * the change on its next tick.
 *
 * The panel is purely presentational: it receives the tables snapshot + the
 * cell-write callback from useSim and loads on mount. There are NO transport
 * controls here — Play/Pause/Reset for the backend model live in the top menu
 * bar's centered transport cluster and operate on the project's id. This panel
 * only shows data.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowClockwise, GraphIcon, Plus, X, Trash } from '@phosphor-icons/react';

type CellValue = string | number | boolean | null;
interface TableRow {
  /** Numeric for rows the app mints; a string for e.g. events rows (uuid ids). */
  id: number | string;
  cells: Record<string, CellValue>;
}
export interface TableColumn {
  name: string;
  type: string;
  /** Column metadata for the insert form: required = NOT NULL without a
   *  default (the backend rejects empty values for these). */
  required?: boolean;
  default?: unknown;
}
interface TableSnapshot {
  name: string;
  columns: TableColumn[];
  /** CAPPED window (first ~100 rows) — the route is polled, and log tables
   *  (events/snapshots) grow unboundedly. `count` is the true total; the
   *  Data tab's paged grid owns full browsing. */
  rows: TableRow[];
  count?: number;
}

interface TablesPanelProps {
  /** The project tables snapshot from useSim. Null while loading. */
  tables: TableSnapshot[] | null;
  /** Last tables error (useSim.tablesState's sibling) — rendered as a banner. */
  error?: string | null;
  /** Dismiss the error banner. */
  onClearError?: () => void;
  /** Reload all tables from the backend. */
  onLoad: () => Promise<void>;
  /** PATCH one cell. The model reads the same DB, so the edit takes effect on
   *  the next tick. */
  onUpdateCell: (table: string, rowId: number | string, column: string, value: CellValue) => Promise<void>;
  onAddTable: (name: string, columns: { name: string; type: string; required?: boolean }[]) => Promise<void>;
  onAddRow: (table: string, cells: Record<string, CellValue>) => Promise<void>;
  onDeleteRow: (table: string, rowId: number | string) => Promise<void>;
  /** Open the table → graph dialog preselected on the active table (the
   *  events→substrate conversion; App owns the dialog since this panel is
   *  purely presentational). */
  onToGraph?: () => void;
  height?: number;
  children?: React.ReactNode;
}

export function TablesPanel({
  tables,
  error,
  onClearError,
  onLoad,
  onUpdateCell,
  onAddTable,
  onAddRow,
  onDeleteRow,
  onToGraph,
  height,
  children,
}: TablesPanelProps): JSX.Element {
  const [activeTable, setActiveTable] = useState<string | null>(null);
  const [showNewTable, setShowNewTable] = useState(false);
  const [newTableName, setNewTableName] = useState('');
  const [newTableCols, setNewTableCols] = useState<{name: string, type: string, required?: boolean}[]>([{ name: 'name', type: 'text' }]);
  // The inline New Record form (open per-table; drafts keyed by column name).
  const [inserting, setInserting] = useState(false);
  const [insertDraft, setInsertDraft] = useState<Record<string, CellValue>>({});
  const [insertError, setInsertError] = useState<string | null>(null);

  // Load on mount ONLY. The parent passes a fresh arrow function for `onLoad`
  // on every render, so depending on it would re-fetch the tables on every
  // render (a request storm). We intentionally use an empty dep array + read
  // the latest callback via a ref. Reload after an edit is handled by the
  // updateTableCell callback itself (it splices the updated row in).
  const onLoadRef = useRef(onLoad);
  onLoadRef.current = onLoad;
  useEffect(() => {
    void onLoadRef.current();
  }, []);

  // Default to the first table once loaded.
  useEffect(() => {
    if (tables && tables.length > 0 && !activeTable) {
      setActiveTable(tables[0].name);
    }
  }, [tables, activeTable]);

  const current = tables?.find((t) => t.name === activeTable) ?? null;

  const isNumeric = (t: string) => t === 'integer' || t === 'float' || t === 'number' || t === 'real';

  const openInsertForm = () => {
    if (!current) return;
    const defaults: Record<string, CellValue> = {};
    current.columns.forEach((c) => {
      if (c.name === 'id') return;
      if (isNumeric(c.type)) defaults[c.name] = typeof c.default === 'number' ? c.default : 0;
      else if (c.type === 'boolean') defaults[c.name] = c.default === true;
      else defaults[c.name] = typeof c.default === 'string' ? c.default : '';
    });
    setInsertDraft(defaults);
    setInsertError(null);
    setInserting(true);
  };

  const submitInsert = () => {
    if (!current) return;
    const missing = current.columns.filter(
      (c) =>
        c.name !== 'id' &&
        c.required &&
        (insertDraft[c.name] === '' || insertDraft[c.name] == null),
    );
    if (missing.length > 0) {
      setInsertError(`Required: ${missing.map((c) => c.name).join(', ')}`);
      return;
    }
    setInserting(false);
    setInsertError(null);
    void onAddRow(current.name, insertDraft);
  };

  return (
    <aside className="tables-panel" style={{ height, minHeight: 300 }}>
      {children}
      {/* Error banner — failed loads/edits surface here instead of vanishing. */}
      {error && (
        <div className="tables-error" role="alert">
          <span className="tables-error-text">{error}</span>
          {onClearError && (
            <button type="button" className="btn-icon-mini btn-ghost" onClick={onClearError} title="Dismiss">
              <X size={13} />
            </button>
          )}
        </div>
      )}
      {/* Header: title + reload + table tabs. Transport controls are NOT here —
          they live in the top menu bar (centered), keyed on the project id. */}
      <div className="tables-header">
        <div className="tables-header-left">
          <h3>Tables</h3>
          <button
            type="button"
            className="btn-icon-mini btn-ghost"
            onClick={() => void onLoad()}
            title="Reload tables"
            aria-label="Reload tables"
          >
            <ArrowClockwise size={13} />
          </button>
          {current && (
            <button type="button" className="btn btn-mini" onClick={openInsertForm} title="New record">
              <Plus size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              New Record
            </button>
          )}
          {onToGraph && current && (
            <button
              type="button"
              className="btn btn-mini"
              onClick={onToGraph}
              title="Convert this table's events into a flow-net graph"
            >
              <GraphIcon size={12} style={{ marginRight: 4, verticalAlign: 'middle' }} />
              To Graph
            </button>
          )}
        </div>
        {tables && tables.length > 0 && (
          <div className="tables-tabs">
            {tables.filter(t => !t.name?.includes('chat_')).map((t) => (
              <button
                key={t.name}
                type="button"
                className={`tables-tab${activeTable === t.name ? ' active' : ''}`}
                onClick={() => {
                  setActiveTable(t.name);
                  setInserting(false);
                }}
              >
                {t.name}
              </button>
            ))}
            <button
              type="button"
              className="tables-tab"
              onClick={() => setShowNewTable(true)}
              title="New table"
            >
              <Plus size={12} />
            </button>
          </div>
        )}
      </div>

      {/* The grid for the active table — horizontal-scrollable so wide tables
          don't blow out the dock height. Rows stay vertical (one record per
          row); the dock itself is capped via max-height in CSS. */}
      {current ? (
        <div className="tables-grid-wrap">
          <div className="tables-grid" role="grid">
            {/* Header row */}
            <div className="tables-row tables-row-header" role="row">
              {current.columns.map((col) => (
                <div 
                  key={col.name} 
                  className="tables-cell tables-cell-header" 
                  role="columnheader" 
                  title={`Type: ${col.type}`}
                  style={col.name === 'id' ? { width: 50, minWidth: 50, flex: 'none' } : {}}
                >
                  {col.name}
                </div>
              ))}
              <div className="tables-cell tables-cell-header" style={{ width: 32, flex: 'none' }} />
            </div>
            {/* Data rows */}
            {current.rows.map((row) => (
              <div key={row.id} className="tables-row" role="row">
                {current.columns.map((col) => {
                  if (col.name === 'id') {
                    return (
                      <div key={col.name} className="tables-cell" style={{ width: 50, minWidth: 50, flex: 'none', color: 'var(--ink-muted)' }}>
                        {row.cells[col.name]}
                      </div>
                    );
                  }
                  return (
                    <EditableCell
                      key={col.name}
                      columnDef={col}
                      tableRows={current.rows}
                      value={row.cells[col.name] ?? null}
                      onCommit={(v) => void onUpdateCell(current.name, row.id, col.name, v)}
                    />
                  );
                })}
                <div className="tables-cell" style={{ width: 32, flex: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <button type="button" className="btn-icon-mini btn-ghost" onClick={() => void onDeleteRow(current.name, row.id)} title="Delete row">
                    <Trash size={14} />
                  </button>
                </div>
              </div>
            ))}
            {/* Inline insert form — typed per-column inputs, required fields
                marked; the backend fills column defaults for omitted values. */}
            {inserting && current && (
              <div className="tables-row tables-row-insert" role="row">
                {current.columns.map((col) => {
                  if (col.name === 'id') {
                    return (
                      <div key={col.name} className="tables-cell" style={{ width: 50, minWidth: 50, flex: 'none', color: 'var(--ink-muted)' }}>
                        <Plus size={12} />
                      </div>
                    );
                  }
                  const req = col.required ? ' *' : '';
                  if (col.type === 'boolean') {
                    return (
                      <div key={col.name} className="tables-cell" style={{ textAlign: 'center' }} title={col.name + req}>
                        <input
                          type="checkbox"
                          checked={Boolean(insertDraft[col.name])}
                          onChange={(e) => setInsertDraft({ ...insertDraft, [col.name]: e.target.checked })}
                        />
                      </div>
                    );
                  }
                  if (isNumeric(col.type)) {
                    return (
                      <div key={col.name} className="tables-cell">
                        <input
                          className="tables-cell-input"
                          type="number"
                          placeholder={col.name + req}
                          value={String(insertDraft[col.name] ?? '')}
                          onChange={(e) =>
                            setInsertDraft({
                              ...insertDraft,
                              [col.name]: e.target.value === '' ? null : Number(e.target.value),
                            })
                          }
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') submitInsert();
                            if (e.key === 'Escape') setInserting(false);
                          }}
                        />
                      </div>
                    );
                  }
                  return (
                    <div key={col.name} className="tables-cell">
                      <input
                        className="tables-cell-input"
                        placeholder={col.name + req}
                        value={String(insertDraft[col.name] ?? '')}
                        autoFocus
                        onChange={(e) => setInsertDraft({ ...insertDraft, [col.name]: e.target.value })}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') submitInsert();
                          if (e.key === 'Escape') setInserting(false);
                        }}
                      />
                    </div>
                  );
                })}
                <div className="tables-cell" style={{ width: 76, flex: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <button type="button" className="btn btn-mini btn-start" onClick={submitInsert} title="Insert row">
                    Add
                  </button>
                  <button type="button" className="btn-icon-mini btn-ghost" onClick={() => setInserting(false)} title="Cancel">
                    <X size={14} />
                  </button>
                </div>
              </div>
            )}
            {inserting && insertError && (
              <div className="tables-insert-error">{insertError}</div>
            )}
          </div>
          {/* Truncation hint — the snapshot carries a capped row window (the
              tables route is polled; log tables are unbounded). */}
          {current.count != null && current.count > current.rows.length && (
            <div className="tables-truncated">
              Showing first {current.rows.length} of {current.count} rows — open the Data tab for the full grid.
            </div>
          )}
        </div>
      ) : (
        <div className="tables-empty">
          {tables === null ? 'Loading tables…' : 'No project tables available.'}
        </div>
      )}

      {showNewTable && (
        <div className="modal-overlay" onClick={() => setShowNewTable(false)}>
          <div className="modal-panel" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>New Table</h2>
              <button type="button" className="btn-icon-mini modal-close" onClick={() => setShowNewTable(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="modal-body">
              <input 
                className="text-input"
                type="text" 
                placeholder="Table name" 
                value={newTableName} 
                onChange={(e) => setNewTableName(e.target.value.replace(/[^a-zA-Z0-9_]/g, ''))}
                style={{ width: '100%', boxSizing: 'border-box' }}
              />
              <div className="sidebar-section" style={{ border: 'none', padding: 0 }}>
                <h3>Columns</h3>
                {newTableCols.map((c, i) => (
                  <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
                    <input 
                      className="text-input"
                      type="text" 
                      placeholder="Column name" 
                      value={c.name} 
                      onChange={(e) => {
                        const copy = [...newTableCols];
                        copy[i].name = e.target.value.replace(/[^a-zA-Z0-9_]/g, '');
                        setNewTableCols(copy);
                      }}
                      style={{ flex: 1 }}
                    />
                    <select
                      className="text-input"
                      value={c.type}
                      onChange={(e) => {
                        const copy = [...newTableCols];
                        copy[i].type = e.target.value;
                        setNewTableCols(copy);
                      }}
                      style={{ width: 110 }}
                    >
                      <option value="text">Text</option>
                      <option value="integer">Integer</option>
                      <option value="float">Float</option>
                      <option value="boolean">Boolean</option>
                      <option value="categorical">Categorical</option>
                    </select>
                    <label
                      title="Required — empty values are rejected on insert"
                      style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: 'var(--ink-muted)' }}
                    >
                      <input
                        type="checkbox"
                        checked={Boolean(c.required)}
                        onChange={(e) => {
                          const copy = [...newTableCols];
                          copy[i].required = e.target.checked;
                          setNewTableCols(copy);
                        }}
                      />
                      req
                    </label>
                    <button 
                      type="button" 
                      className="btn-icon-mini btn-ghost" 
                      style={{ width: 28, height: 28, border: '1px solid var(--rule)', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      onClick={() => setNewTableCols(newTableCols.filter((_, idx) => idx !== i))}
                    >
                      <Trash size={14} />
                    </button>
                  </div>
                ))}
                <button 
                  type="button" 
                  className="btn btn-mini" 
                  style={{ marginTop: 4, alignSelf: 'flex-start' }}
                  onClick={() => setNewTableCols([...newTableCols, {name: '', type: 'text'}])}
                >
                  <Plus size={12} style={{ marginRight: 4 }} /> Add Column
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn" onClick={() => setShowNewTable(false)}>
                Cancel
              </button>
              <button 
                type="button" 
                className="btn btn-start" 
                disabled={!newTableName || newTableCols.length === 0}
                onClick={() => {
                  if (newTableName && newTableCols.length > 0) {
                    void onAddTable(newTableName, newTableCols);
                    setShowNewTable(false);
                    setNewTableName('');
                    setNewTableCols([{ name: 'name', type: 'text' }]);
                  }
                }}
              >
                Create Table
              </button>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

/** One editable cell. Click to edit; Enter/blur commits; Esc cancels. */
function EditableCell({
  columnDef,
  tableRows,
  value,
  onCommit,
}: {
  columnDef: TableColumn;
  tableRows: TableRow[];
  value: CellValue;
  onCommit: (v: CellValue | boolean) => void;
}): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(value ?? ''));

  const isBoolean = columnDef.type === 'boolean';
  const isCategorical = columnDef.type === 'categorical';
  const isNumber = columnDef.type === 'integer' || columnDef.type === 'float';

  if (isBoolean) {
    return (
      <div className="tables-cell" role="gridcell" title="Click to toggle" onClick={() => onCommit(!value)} style={{ cursor: 'pointer', textAlign: 'center' }}>
        <input type="checkbox" checked={Boolean(value)} onChange={(e) => onCommit(e.target.checked)} onClick={e => e.stopPropagation()} />
      </div>
    );
  }

  if (!editing) {
    return (
      <div
        className="tables-cell"
        role="gridcell"
        onClick={() => {
          setDraft(String(value ?? ''));
          setEditing(true);
        }}
        title="Click to edit"
      >
        {value ?? '—'}
      </div>
    );
  }

  const commit = () => {
    setEditing(false);
    const original = String(value ?? '');
    if (draft !== original) {
      if (isNumber) {
        const num = Number(draft);
        onCommit(draft.trim() !== '' && Number.isFinite(num) ? num : draft);
      } else {
        onCommit(draft);
      }
    }
  };

  const commonProps = {
    className: "tables-cell-input",
    autoFocus: true,
    value: draft,
    onChange: (e: any) => setDraft(e.target.value),
    onBlur: commit,
    onKeyDown: (e: any) => {
      if (e.key === 'Enter') commit();
      if (e.key === 'Escape') setEditing(false);
    }
  };

  if (isCategorical) {
    const options = Array.from(new Set(tableRows.map(r => r.cells[columnDef.name]).filter(v => typeof v === 'string' && v !== '')));
    const listId = `dl-${columnDef.name}`;
    return (
      <>
        <input {...commonProps} list={listId} />
        <datalist id={listId}>
          {options.map(opt => <option key={String(opt)} value={String(opt)} />)}
        </datalist>
      </>
    );
  }

  if (isNumber) {
    return <input {...commonProps} type="number" />;
  }

  return <input {...commonProps} />;
}
